'use client';

import { useState } from 'react';
import { Shield, X, Plus, ChevronDown, Lightbulb, Search } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Tip } from '@/components/ui/tooltip';

type TrustRules = string[] | { allow: string[]; deny?: string[] };

// Every value this panel can write must have a matcher in `evaluateAgentTrust`
// (lib/agents/visibility.ts): the presets `everyone`, `family`, `nobody`, an
// `@handle` and an `@glob` with `*` or `**`. The rule names AGENTS: "My
// Agents" is the agents this person owns, "Specific" is agents by username.
// The advanced field once offered `#verified` and `#premium` label chips and
// advertised them in its placeholder; nothing has ever matched a `#` token,
// so an owner who chose one had written "No One" without knowing. Removed
// 2026-09-07; tests/unit/components/trust-settings-tokens.test.ts pins that
// nothing this file emits is outside the evaluator's vocabulary.

interface TrustSettingsProps {
  acceptFrom: TrustRules | null;
  talkTo: TrustRules | null;
  onChange: (field: 'acceptFrom' | 'talkTo', value: TrustRules | null) => void;
  hideTitle?: boolean;
  mentionedAgents?: string[];
  compact?: boolean;
}

function rulesAsList(rules: TrustRules | null): string[] {
  if (!rules) return [];
  if (Array.isArray(rules)) return rules;
  return rules.allow || [];
}

function rulesDenyList(rules: TrustRules | null): string[] {
  if (!rules || Array.isArray(rules)) return [];
  return rules.deny || [];
}

function activePreset(rules: TrustRules | null): string | null {
  const list = rulesAsList(rules);
  const deny = rulesDenyList(rules);
  if (deny.length > 0) return null;
  if (list.length === 1 && list[0] === 'everyone') return 'everyone';
  if (list.length === 1 && list[0] === 'family') return 'family';
  if (list.length === 1 && list[0] === 'nobody') return 'nobody';
  if (list.length === 0) return null;
  if (list.some(r => r.startsWith('@') || r.startsWith('#') || r.includes('*'))) return 'specific';
  return null;
}

function TrustPanel({
  label,
  rules,
  onChange,
  mentionedAgents,
  compact,
}: {
  label: string;
  rules: TrustRules | null;
  onChange: (value: TrustRules | null) => void;
  mentionedAgents?: string[];
  compact?: boolean;
}) {
  const t = useT();
  const [newPattern, setNewPattern] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const preset = activePreset(rules);
  const allowList = rulesAsList(rules);
  const denyList = rulesDenyList(rules);
  const [forceSpecific, setForceSpecific] = useState(false);
  const isSpecific = forceSpecific || preset === 'specific' || (!preset && allowList.length >= 0 && allowList.every(r => !['everyone', 'family', 'nobody'].includes(r)) && (allowList.length > 0 || forceSpecific));

  const PRESETS = [
    { value: 'everyone', label: t('agents.trustEveryone'), desc: 'Anyone on the platform' },
    { value: 'family', label: t('agents.trustMyAgents'), desc: 'Only agents you own' },
    { value: 'specific', label: t('agents.trustSpecific'), desc: 'Choose specific agents' },
    { value: 'nobody', label: t('agents.trustNoOne'), desc: 'Disable completely' },
  ] as const;

  const selectPreset = (p: string) => {
    if (p === 'specific') {
      setForceSpecific(true);
      const currentSpecific = allowList.filter(r => !['everyone', 'family', 'nobody'].includes(r));
      onChange(currentSpecific.length > 0 ? currentSpecific : []);
      return;
    }
    setForceSpecific(false);
    onChange([p]);
    setShowAdvanced(false);
  };

  const addAgent = (agent: string) => {
    const trimmed = agent.trim();
    if (!trimmed || allowList.includes(trimmed)) return;
    const newAllow = [...allowList.filter(r => !['everyone', 'family', 'nobody'].includes(r)), trimmed];
    if (denyList.length > 0) {
      onChange({ allow: newAllow, deny: denyList });
    } else {
      onChange(newAllow);
    }
    setNewPattern('');
    setSearchQuery('');
  };

  const removeAgent = (agent: string) => {
    const newAllow = allowList.filter(r => r !== agent);
    if (denyList.length > 0) {
      onChange({ allow: newAllow, deny: denyList });
    } else {
      onChange(newAllow.length > 0 ? newAllow : null);
    }
  };

  const unmatchedMentions = (mentionedAgents || []).filter(m => !allowList.includes(m));
  const showMentionSuggestion = unmatchedMentions.length > 0 && preset !== 'everyone';

  const addAllMentioned = () => {
    if (preset === 'family' || preset === 'nobody' || !preset) {
      const newList = [...unmatchedMentions];
      onChange(newList);
    } else {
      const combined = [...allowList, ...unmatchedMentions.filter(m => !allowList.includes(m))];
      if (denyList.length > 0) {
        onChange({ allow: combined, deny: denyList });
      } else {
        onChange(combined);
      }
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium">{label}</h4>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => {
          const isActive = p.value === 'specific' ? isSpecific : preset === p.value;
          return (
            <Tip key={p.value} label={p.desc}>
            <button
              type="button"
              onClick={() => selectPreset(p.value)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                isActive
                  ? 'bg-primary/20 border-primary text-primary font-medium'
                  : 'border-border hover:border-primary/30 text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
            </Tip>
          );
        })}
      </div>

      {showMentionSuggestion && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-sm">
          <Lightbulb className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-muted-foreground">
              {t('agents.trustInstructionsMention', { agents: unmatchedMentions.join(', ') })}
            </span>
            <button
              type="button"
              onClick={addAllMentioned}
              className="ml-2 text-primary text-xs font-medium hover:underline"
            >
              {t('agents.trustAddMentioned')}
            </button>
          </div>
        </div>
      )}

      {isSpecific && (
        <div className="space-y-2 pl-1">
          {allowList.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {allowList.map((r) => (
                <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                  {r}
                  <button type="button" onClick={() => removeAgent(r)} className="hover:text-primary/70">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = searchQuery.startsWith('@') ? searchQuery : `@${searchQuery}`;
                    addAgent(val);
                  }
                }}
                placeholder={t('agents.trustAddAgentPlaceholder')}
                className="input flex-1 h-8 text-xs pl-8 w-full"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const val = searchQuery.startsWith('@') ? searchQuery : `@${searchQuery}`;
                addAgent(val);
              }}
              disabled={!searchQuery.trim()}
              className="btn-secondary h-8 px-2 text-xs"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            {t('agents.trustAdvancedPatterns')}
          </button>

          {showAdvanced && (
            <div className="space-y-2 pl-2 border-l border-border/50">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newPattern}
                  onChange={(e) => setNewPattern(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addAgent(newPattern); }
                  }}
                  placeholder={t('agents.trustAdvancedPatternsPlaceholder')}
                  className="input flex-1 h-8 text-xs"
                />
                <button
                  type="button"
                  onClick={() => addAgent(newPattern)}
                  disabled={!newPattern.trim()}
                  className="btn-secondary h-8 px-2 text-xs"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function extractMentions(instructions: string): string[] {
  const matches = instructions.match(/(?<=^|\s)@[\w.-]+/g) || [];
  return [...new Set(matches.map(m => m.trim()))].filter(m => m !== '@' && m.length > 1);
}

export default function TrustSettings({ acceptFrom, talkTo, onChange, hideTitle, mentionedAgents, compact }: TrustSettingsProps) {
  const t = useT();
  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {!hideTitle && (
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">{t('agents.trustTitle')}</h3>
        </div>
      )}

      <TrustPanel
        label={t('agents.trustInbound')}
        rules={acceptFrom}
        onChange={(v) => onChange('acceptFrom', v)}
        compact={compact}
      />

      <TrustPanel
        label={t('agents.trustOutbound')}
        rules={talkTo}
        onChange={(v) => onChange('talkTo', v)}
        mentionedAgents={mentionedAgents}
        compact={compact}
      />
    </div>
  );
}
